import { useState, type FormEvent } from 'react';
import { api } from '@mmef/shared/api';
import { Callout } from '@mmef/shared/components';

/**
 * Countries worth offering as an answer. Deliberately short — a list of two hundred turns a
 * one-second question into a scroll, and these are the ones with the populations this site serves.
 */
const COUNTRIES = [
  { code: 'IE', name: 'Ireland' },
  { code: 'US', name: 'United States' },
  { code: 'CA', name: 'Canada' },
  { code: 'AU', name: 'Australia' },
  { code: 'ZA', name: 'South Africa' },
  { code: 'FR', name: 'France' },
  { code: 'DE', name: 'Germany' },
  { code: 'NL', name: 'Netherlands' },
  { code: 'SE', name: 'Sweden' },
  { code: 'MY', name: 'Malaysia' },
  { code: 'OTHER', name: 'Somewhere else' },
] as const;

const countryInterestApi = api.injectEndpoints({
  endpoints: (build) => ({
    registerCountryInterest: build.mutation<
      { message: string },
      { country: string; email?: string; note?: string }
    >({
      query: (body) => ({ url: '/country-interest', method: 'POST', body }),
    }),
  }),
});

const { useRegisterCountryInterestMutation } = countryInterestApi;

/**
 * "We only cover the UK — where are you?"
 *
 * <p>Collapsed to a single line until someone opens it, because a modal that interrupts a search
 * to ask a question the visitor didn't raise is an interruption, not an invitation. The people
 * worth hearing from will click; everyone else scrolls past a sentence.
 *
 * <p>No account required, deliberately. Whoever can best tell us about events in Toronto has no
 * reason to have signed up to a site that doesn't cover Toronto.
 */
export function CountryInterestPrompt() {
  const [open, setOpen] = useState(false);
  const [country, setCountry] = useState('');
  const [email, setEmail] = useState('');
  const [note, setNote] = useState('');
  const [register, { isLoading, isSuccess, data }] = useRegisterCountryInterestMutation();

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!country) return;
    register({
      country: country === 'OTHER' ? 'ZZ' : country,
      email: email.trim() || undefined,
      // "Somewhere else" is useless on its own, so the note carries the answer instead.
      note: note.trim() || (country === 'OTHER' ? 'Country not in the list' : undefined),
    });
  };

  if (isSuccess) {
    return (
      <Callout tone="success" title="Thanks">
        {data?.message ?? 'Noted — we’ll look at your country as demand grows.'}
      </Callout>
    );
  }

  if (!open) {
    return (
      <p className="country-prompt">
        Outside the UK?{' '}
        <button type="button" className="button button--link" onClick={() => setOpen(true)}>
          Tell us where you are
        </button>{' '}
        and we&apos;ll look at covering it.
      </p>
    );
  }

  return (
    <form className="country-prompt country-prompt--open" onSubmit={onSubmit}>
      <p className="field__hint">
        {/* Honest about what this is: a signal that decides an order, not a promise. */}
        We only list UK events today. Tell us where you are and we&apos;ll know where to look next —
        no promises about when.
      </p>

      <div className="field">
        <label className="field__label" htmlFor="ci-country">
          Your country
        </label>
        <select
          id="ci-country"
          className="field__input"
          value={country}
          onChange={(e) => setCountry(e.target.value)}
          required
        >
          <option value="">Choose…</option>
          {COUNTRIES.map((c) => (
            <option key={c.code} value={c.code}>
              {c.name}
            </option>
          ))}
        </select>
      </div>

      <div className="field">
        <label className="field__label" htmlFor="ci-note">
          Anything useful? (optional)
        </label>
        <input
          id="ci-note"
          className="field__input"
          maxLength={500}
          placeholder="Which city, or who runs events there"
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />
      </div>

      <div className="field">
        <label className="field__label" htmlFor="ci-email">
          Email (optional)
        </label>
        <input
          id="ci-email"
          className="field__input"
          type="email"
          maxLength={255}
          placeholder="Only if you want telling when we do"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
      </div>

      <div className="form__actions">
        <button className="button button--primary" type="submit" disabled={isLoading || !country}>
          {isLoading ? 'Sending…' : 'Register interest'}
        </button>
        <button type="button" className="button button--link" onClick={() => setOpen(false)}>
          Cancel
        </button>
      </div>
    </form>
  );
}
